import { Box, IconButton, Modal, Paper, useTheme } from '@mui/material';
import { Typography } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { Button, Icon } from '@verantos-dev/shared-components';

import { ATTR_PRESENT_NAME } from '../../../workspace-common/constants';
import type { Concept } from '../../../workspace-common/types/responseTypes';
import { useBulkCreateAnnotationsMutation } from '../../api/annotationApiSlice';

interface MarkNotPresentModalProps {
  showMarkRemainingNotPresentModal: boolean;
  setShowMarkRemainingNotPresentModal: (showMarkRemainingNotPresentModal: boolean) => void;
  namesOfConceptsWithoutAnnotationsArray: string[];
  conceptsWithoutAnnotations: Concept[];
  dirtyConceptNames: string[];
}

export const MarkNotPresentModal = ({
  showMarkRemainingNotPresentModal,
  setShowMarkRemainingNotPresentModal,
  namesOfConceptsWithoutAnnotationsArray,
  conceptsWithoutAnnotations,
  dirtyConceptNames,
}: MarkNotPresentModalProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { taskId } = useParams();
  const [bulkCreateAnnotations, { isLoading: isMarkingRemainingNotPresentLoading }] =
    useBulkCreateAnnotationsMutation();
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!taskId || conceptsWithoutAnnotations.length === 0) {
      return;
    }

    try {
      setError(null);

      // Prepare annotations with present: false for all concepts
      const annotations = conceptsWithoutAnnotations.map((concept) => {
        // Get all attributes for this concept and set present to false
        // For other attributes, set them to undefined (backend will handle defaults)
        const attributes = concept.attributes.map((attribute) => {
          if (attribute.attributeName === ATTR_PRESENT_NAME) {
            return {
              name: attribute.attributeName,
              value: false,
              supportedIndirectly: false,
              unknown: false,
            };
          } else {
            // For all other attributes, send undefined values
            return {
              name: attribute.attributeName,
              value: undefined,
              supportedIndirectly: false,
              unknown: false,
            };
          }
        });

        return {
          conceptId: concept.conceptId,
          attributes,
          spans: [],
        };
      });

      await bulkCreateAnnotations({
        taskId,
        annotations,
      }).unwrap();

      // Close modal on success
      setShowMarkRemainingNotPresentModal(false);
    } catch (err: any) {
      console.error('Failed to create annotations:', err);
      // Extract error message from API response
      const errorMessage =
        err?.data?.detail ||
        err?.data?.message ||
        err?.data?.errorMessage ||
        err?.message ||
        'Failed to mark concepts as not present';
      setError(errorMessage);
    }
  };
  const unsavedAnnotationsPresent = dirtyConceptNames.length > 0;
  return (
    <Modal
      open={showMarkRemainingNotPresentModal}
      onClose={() => setShowMarkRemainingNotPresentModal(false)}
      aria-labelledby="mark-remaining-not-present-modal-title"
    >
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          p: theme.spacing(6),
          outline: 'none',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography id="mark-remaining-not-present-modal-title" variant="h6">
              {unsavedAnnotationsPresent
                ? formatMessage({
                    id: 'annotationContent.markNotPresentModal.unSavedAnnotations.title',
                  })
                : formatMessage({ id: 'annotationContent.markNotPresentModal.title' })}
            </Typography>
            <IconButton onClick={() => setShowMarkRemainingNotPresentModal(false)} size="small">
              <Icon id="mark-remaining-not-present-modal-close-icon" icon="clear" width={16} />
            </IconButton>
          </Box>
          {unsavedAnnotationsPresent ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                {formatMessage({
                  id: 'annotationContent.markNotPresentModal.unsavedAnnotations.description',
                })}
              </Typography>
              <Box>
                <ul
                  style={{
                    listStyleType: 'none',
                    margin: 0,
                    padding: 0,
                    maxHeight: '500px',
                    overflow: 'scroll',
                  }}
                >
                  {dirtyConceptNames.map((conceptName) => (
                    <li
                      key={conceptName}
                      style={{
                        border: `1px solid ${theme.palette.divider}`,
                        padding: theme.spacing(2),
                      }}
                    >
                      <Box sx={{ display: 'flex', p: theme.spacing(2) }}>
                        <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                          {conceptName}
                        </Typography>
                      </Box>
                    </li>
                  ))}
                </ul>
              </Box>
              <Typography variant="body1">
                {formatMessage({
                  id: 'annotationContent.markNotPresentModal.unsavedAnnotations.description2',
                })}
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  gap: theme.spacing(3),
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  id="ok-save-or-discard-unsaved-annotations-button"
                  displayLabel={formatMessage({
                    id: 'annotationContent.markNotPresentModal.unsavedAnnotations.okButton',
                  })}
                  variant="contained"
                  sx={{
                    fontSize: '15px',
                    textTransform: 'none',
                    backgroundColor: theme.palette.primary.dark,
                  }}
                  onClickHandler={() => setShowMarkRemainingNotPresentModal(false)}
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                {formatMessage({ id: 'annotationContent.markNotPresentModal.description' })}
              </Typography>
              <Box>
                <ul
                  style={{
                    listStyleType: 'none',
                    margin: 0,
                    padding: 0,
                    maxHeight: '500px',
                    overflow: 'scroll',
                  }}
                >
                  {namesOfConceptsWithoutAnnotationsArray.map((conceptName) => (
                    <li
                      key={conceptName}
                      style={{
                        border: `1px solid ${theme.palette.divider}`,
                        padding: theme.spacing(2),
                      }}
                    >
                      <Box sx={{ display: 'flex', p: theme.spacing(2) }}>
                        <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                          {conceptName}
                        </Typography>
                      </Box>
                    </li>
                  ))}
                </ul>
              </Box>
              {isMarkingRemainingNotPresentLoading && (
                <Typography variant="body1">
                  {formatMessage({
                    id: 'annotationContent.annotationConfig.markRemainingNotPresent.pendingMsg',
                  })}
                </Typography>
              )}
              {error && (
                <Typography
                  variant="body1"
                  sx={{ color: theme.palette.error.main, width: '100%', textAlign: 'center' }}
                >
                  {error}
                </Typography>
              )}
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  gap: theme.spacing(3),
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  id="cancel-mark-remaining-not-present-button"
                  displayLabel={formatMessage({
                    id: 'annotationContent.markNotPresentModal.cancelButton',
                  })}
                  variant="text"
                  sx={{
                    fontSize: '15px',
                    textTransform: 'none',
                  }}
                  onClickHandler={() => setShowMarkRemainingNotPresentModal(false)}
                />
                <Button
                  id="confirm-mark-remaining-not-present-button"
                  displayLabel={formatMessage({
                    id: 'annotationContent.markNotPresentModal.confirmButton',
                  })}
                  variant="contained"
                  sx={{
                    fontSize: '15px',
                    textTransform: 'none',
                    backgroundColor: theme.palette.primary.dark,
                  }}
                  disabled={isMarkingRemainingNotPresentLoading}
                  onClickHandler={handleConfirm}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Modal>
  );
};
